import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  useToastStack,
  TOAST_BASE_OFFSET,
  TOAST_BASE_OFFSET_WITH_COOKIE_BANNER,
  TOAST_STACK_SLOT_HEIGHT
} from "../context/ToastStackContext";

const COOKIE_CONSENT_KEY = "virsa_cookie_consent";
let nextToastId = 0;

export default function Toast({ text, onClose }) {

  // Keep the latest onClose without making the auto-dismiss timer restart
  // every time the parent re-renders and passes a new inline function.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // A stable, unique id for this mounted instance's whole lifetime — the
  // functional initializer form runs exactly once, unlike useRef(expr)
  // where the expression itself re-evaluates (and would bump the counter)
  // on every render.
  const [id] = useState(() => ++nextToastId);
  const stack = useToastStack();

  // Read once on mount, same as UndoToast.jsx — a first-time visitor who
  // triggers ANY toast before dismissing the cookie banner would otherwise
  // have it draw right inside the banner's own space: this is a generic,
  // ~15-call-site component, so the banner being up is a real, reachable
  // case, not just UndoToast's own soft-delete flow.
  const [clearCookieBanner] = useState(() => {
    try {
      return localStorage.getItem(COOKIE_CONSENT_KEY) !== "1";
    } catch {
      return false;
    }
  });

  // Registration is a write to an ANCESTOR's state (the shared provider),
  // which has to happen in an effect, not during render — this is also why
  // `stack?.ids` (read below) only reflects other toasts, not this one,
  // until after the first commit.
  useEffect(() => {
    stack?.register(id);
    return () => stack?.unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Without `text` in the deps, a caller that replaces one toast's message
  // with another on the SAME mounted instance (e.g. LikeButton swapping
  // "Liked" for "Like removed" on a quick undo) never re-arms this timer —
  // the new message inherits whatever was left of the old one's countdown,
  // sometimes showing for barely a moment before vanishing.
  useEffect(() => {

    const t = setTimeout(() => onCloseRef.current(), 2500);

    return () => clearTimeout(t);

  }, [text]);

  // No provider mounted (defensive — every page is expected to have one via
  // ToastStackProvider near the root) or not registered yet: falls back to
  // the original single fixed position, same as before this existed.
  const index = stack ? stack.ids.indexOf(id) : -1;
  const stackOffset = index > 0 ? index * TOAST_STACK_SLOT_HEIGHT : 0;
  const base = clearCookieBanner ? TOAST_BASE_OFFSET_WITH_COOKIE_BANNER : TOAST_BASE_OFFSET;

  // Every other overlay in this app (FollowListModal, ReportModal,
  // NotificationBell, ImageLightbox, ...) portals to document.body — Toast
  // was the one exception. Rendered in place instead, position:fixed here
  // is at the mercy of every ancestor between this and the page root: CSS
  // transform/filter/perspective/will-change on ANY of them creates a new
  // containing block for fixed descendants, silently turning "fixed to the
  // viewport" into "fixed to that ancestor's box" instead — which is
  // exactly why this could show up glued to the middle of a specific card
  // (one with an animating/transformed ancestor) rather than at the bottom
  // of the screen. Toast is called from deep inside arbitrarily-nested
  // components (LikeButton, SaveButton, ReportModal's own reason list, ...),
  // so there's no reliable way to guarantee none of their ancestors ever
  // apply one of those properties — portaling sidesteps the whole class of
  // failure instead of chasing down which ancestor did it this time.
  return createPortal(
    <div style={{ ...toast, bottom: `calc(${base}px + env(safe-area-inset-bottom) + ${stackOffset}px)` }}>
      {text}
    </div>,
    document.body
  );
}

const toast = {

  position: "fixed",

  // `bottom` itself is set inline above — 78px normally clears the floating
  // pill nav (bottom:30 alone overlapped it by ~38px, same bug fixed in
  // UndoToast.jsx), 150px when the cookie banner is still up, plus a
  // per-toast stacking offset on top of whichever base applies.

  left: "50%",

  transform: "translateX(-50%)",

  background: "#000",

  color: "#fff",

  padding: "10px 16px",

  borderRadius: 10,

  fontSize: 14,

  zIndex: 9999,

  // When a toast below this one in the stack dismisses first, this one
  // settles down to fill the gap — without a transition it just snaps
  // straight to the new position instead of sliding.
  transition: "bottom 0.2s ease",

  boxShadow: "0 10px 30px rgba(0,0,0,0.25)"
};